import { isNumber, isUndefinedOrNull } from "../../omnia"
import { INtrReq, NutrientType } from "../../types"
import { NutrientDef } from "./nutrient-def"

/// <reference path="nutrient-def.ts" />

export type NutritentChangeType = "resetToCalculated" | undefined

type Watcher = (ntr: NutrientViewModel, change: NutritentChangeType) => void

export class NutrientViewModel {

    public key: string;
    public htmlText: string;
    public hasOpeningBalance: boolean;

    public calcStatusText: string;

    private _calcValue: number;
    private _initValue: number;
    private _hasCalcValue: boolean;

    private _soilAnalysisNew: boolean;
    private _customReq: boolean;

    private _balance: number;
    private _initBalance: number;

    private _defaultBalance: number;
    private _customBalance: boolean;

    private _grossValue: number;
    private _calcGrossValueBalance: number;


    constructor(private def: NutrientDef, req: INtrReq, private _canEdit: boolean, private _watcher: Watcher) {

        this._watcher = this._watcher || ((vm: NutrientViewModel) => { });

        let key = def.key;

        this.key = def.key;

        this._initValue = req["Required" + key];
        this._calcValue = req["CalcRequired" + key];
        this._calcValue = isNumber(this._calcValue) ? this._calcValue : this._initValue;
        this.calcStatusText = def.getCalcStatus(req);
        this._hasCalcValue = !this.calcStatusText;

        this.hasOpeningBalance = def.hasOpeningBalance;
        if (this.hasOpeningBalance) {
            this.setupBalance(req);
        }

        this._calcGrossValueBalance = (this._soilAnalysisNew || isUndefinedOrNull(this._balance)) ? null : this._defaultBalance;
        this._grossValue = isNumber(this._initValue) || isNumber(this.openingBalance) ? (this._initValue || 0) + (this.openingBalance || 0) : null;

        this.updateStatus(false);

        this.htmlText = def.htmlText;

        if (this.hasError()) {
            this.htmlText += " <i class='fas fa-exclamation-triangle warning'></i>";
        }

    }

    private setupBalance(req: INtrReq) {

        let key = this.def.key;

        this._soilAnalysisNew = req.SoilAnalysisNew && (key != "N");

        this._balance = req["OpeningBalance" + key];
        this._initBalance = this._balance;

        this._defaultBalance = req["InitOpeningBalance" + key];
        this._customBalance = isNumber(this._defaultBalance) && (this._balance != this._defaultBalance);
        this._defaultBalance = (this._customBalance ? this._defaultBalance : this._balance);

    }

    get nutrientType(): NutrientType {
        return this.def.nutrientType;
    }

    get nutrientMask(): number {
        return Math.pow(2, this.def.nutrientType - 1);
    }

    get value(): number {
        let v: number;
        if (isNumber(this._grossValue) || isNumber(this.openingBalance)) {
            v = (this._grossValue || 0) - (this.openingBalance || 0);
        }
        return v;
    }

    setValue(value: number, notify: boolean) {
        this._grossValue = value + (this.openingBalance || 0);
        this.updateStatus(notify);
    }

    private updateStatus(notify: boolean = true, changeType?: NutritentChangeType) {

        this._customReq = (this.value != this._calcValue);
        this._customBalance = (this.openingBalance != this._defaultBalance);

        if (notify) {
            this._watcher(this, changeType);
        }

    }

    getEffectiveValue(): number {
        if ((!this._customReq) && this._hasCalcValue) return null;
        return this.value;
    }

    showInitialGrossValue(): boolean {
        if (this._canEdit && this._customReq) {
            return ((this._grossValue || 0) != ((this._calcValue || 0) + (this._calcGrossValueBalance || 0)));
        }
    }

    isCustom(): boolean {
        if (this._canEdit) {
            return this._customReq || this._customBalance;
        }
    }

    hasError(): boolean {
        return !!this.calcStatusText;
    }

    get grossValue(): number {
        return this._grossValue;
    }

    set grossValue(value: number) {
        this._grossValue = value;
        this.updateStatus();
    }

    get calculatedGrossValue(): number {
        return (this._calcValue || 0) + (this._calcGrossValueBalance || 0);
    }

    resetToCalculated() {
        this._grossValue = this._calcValue + (this._calcGrossValueBalance || 0);
        this.updateStatus(true, "resetToCalculated");
    }

    resetToInitial(notify: boolean) {
        this._grossValue = this._initValue + (this._defaultBalance || 0);
        this.updateStatus(notify);
    }

    getCssClass(): any {
        var r = {};
        r["ntr-" + this.key.toLocaleLowerCase()] = true;
        return r;
    }

    get tonnesPerHa(): boolean {
        return this.def.tonnesPerHa;
    }

    get openingBalance(): number {
        return this._balance;
    }

    set openingBalance(value: number) {
        this._balance = value;
        this.updateStatus();
    }

    hasOpeningBalanceChanged(): boolean {
        return (this._balance != this._initBalance);
    }

    hasCustomBalance(): boolean {
        return this._customBalance;
    }

    get defaultOpeningBalance(): number {
        return this._defaultBalance;
    }

    resetBalanceToDefault() {
        this.openingBalance = this._defaultBalance;
    }

    isSoilAnalysisNew(): boolean {
        return this._soilAnalysisNew;
    }

    canEdit(): boolean {
        return this._canEdit;
    }

    public hasChanged(): boolean {
        let r = ((this.value || 0) != (this._initValue || 0)) || this.hasOpeningBalanceChanged();
        return r;
    }

    get analysisAttribute(): string {
        return this.def.analysisAttribute;
    }

    get rounding(): number {
        return this.def.rounding;
    }

    canResetBalance(): boolean {
        return this._canEdit && (!this._soilAnalysisNew) && this._customBalance && (this._balance != this._defaultBalance);
    }

}

